import type { Direction } from "../../engine/direction";
import type { ParticleEmitter } from "../../engine/interfaces";
import type { ParticleType } from "../../engine/particleType";
import type { PhysicalThing } from "../../engine/physicalThing";
import type { CanvasLayer, FrameContext } from "../canvasLayer";
import type { Particle } from "./particle";
import { type ParticleTypeConfig, getParticleTypeConfig } from "./particleFactory";

const DEFAULT_EMIT_INTERVAL_MS = 50;

interface GeneratorEntry {
	config: ParticleTypeConfig;
	emitIntervalMs: number;
	timeSinceEmit: number;
}

export class ParticleRenderer implements CanvasLayer {
	private particles: Particle[] = [];
	private generators: Map<PhysicalThing, GeneratorEntry> = new Map();

	spawnBurst(originX: number, originY: number, type: ParticleType, count: number, direction?: Direction): void {
		const config = getParticleTypeConfig(type);
		// Center on the cell (board coords are top-left, particles should emit from center)
		const cx = originX + 0.5;
		const cy = originY + 0.5;
		for (let i = 0; i < count; i++) {
			this.particles.push(config.create(cx, cy, direction));
		}
	}

	syncGenerators(emitters: { entity: PhysicalThing; type: ParticleType }[]): void {
		const activeEntities = new Set(emitters.map((e) => e.entity));

		// Remove generators whose entity is no longer in the set
		for (const entity of this.generators.keys()) {
			if (!activeEntities.has(entity)) {
				this.generators.delete(entity);
			}
		}

		// Add or update generators for emitting entities
		for (const { entity, type } of emitters) {
			const newConfig = getParticleTypeConfig(type);
			const existing = this.generators.get(entity);
			if (!existing) {
				this.generators.set(entity, {
					config: newConfig,
					emitIntervalMs: newConfig.emitIntervalMs ?? DEFAULT_EMIT_INTERVAL_MS,
					timeSinceEmit: 0,
				});
			} else if (existing.config !== newConfig) {
				existing.config = newConfig;
				existing.emitIntervalMs = newConfig.emitIntervalMs ?? DEFAULT_EMIT_INTERVAL_MS;
			}
		}
	}

	update(rawElapsedMs: number, entityPositionResolver: (entity: PhysicalThing) => { x: number; y: number }): void {
		// Advance particle elapsed times and remove expired ones
		for (const particle of this.particles) {
			particle.elapsed += rawElapsedMs;
		}
		this.particles = this.particles.filter((p) => !p.isExpired());

		// Emit particles from generators
		for (const [entity, gen] of this.generators) {
			gen.timeSinceEmit += rawElapsedMs;

			// Scale emission rate by entity intensity (if available)
			const intensity =
				"getParticleIntensity" in entity
					? (entity as unknown as ParticleEmitter).getParticleIntensity!()
					: 1.0;
			const effectiveInterval = intensity > 0 ? gen.emitIntervalMs / intensity : Infinity;

			const pos = entityPositionResolver(entity);
			const direction: Direction | undefined = "direction" in entity ? (entity as any).direction : undefined;

			const offset = gen.config.spawnOffset?.(direction) ?? { x: 0, y: 0 };
			// Center on the cell + apply spawn offset
			const originX = pos.x + 0.5 + offset.x;
			const originY = pos.y + 0.5 + offset.y;

			while (gen.timeSinceEmit >= effectiveInterval) {
				this.particles.push(gen.config.create(originX, originY, direction));
				gen.timeSinceEmit -= effectiveInterval;
			}
		}
	}

	render(ctx: CanvasRenderingContext2D, frame: FrameContext): void {
		const camera = frame.camera;
		for (const particle of this.particles) {
			const posOffset = particle.positionFn(particle.elapsed, particle.ttl);
			const cellX = particle.originX + posOffset.x;
			const cellY = particle.originY + posOffset.y;

			const screenX = cellX * camera.cellWidth - camera.offsetX;
			const screenY = cellY * camera.cellHeight - camera.offsetY;

			const size = particle.sizeFn(particle.elapsed, particle.ttl);
			const opacity = particle.opacityFn(particle.elapsed, particle.ttl);
			const color = particle.colorFn(particle.elapsed, particle.ttl);
			const rotation = particle.rotationFn(particle.elapsed, particle.ttl);

			const pixelSize = size * camera.cellWidth;

			if (pixelSize <= 0 || opacity <= 0) continue;

			ctx.globalAlpha = opacity;
			ctx.fillStyle = `rgb(${color.r},${color.g},${color.b})`;

			ctx.save();
			ctx.translate(screenX, screenY);
			ctx.rotate(rotation);
			ctx.fillRect(-pixelSize / 2, -pixelSize / 2, pixelSize, pixelSize);
			ctx.restore();
		}

		ctx.globalAlpha = 1.0;
	}

	clear(): void {
		this.particles = [];
		this.generators.clear();
	}

	hasParticles(): boolean {
		return this.particles.length > 0 || this.generators.size > 0;
	}

	get particleCount(): number {
		return this.particles.length;
	}

	get generatorCount(): number {
		return this.generators.size;
	}
}
